/**
 * PrivacyPolicy.tsx — Public privacy policy.
 * Rebuilt in Pass 7. Content preserved from the original; uses shared LegalPageLayout.
 */

import { LegalPageLayout, Section, Bullets, A, L } from './LegalPageLayout';

export default function PrivacyPolicy() {
  return (
    <LegalPageLayout pageTitle="Privacy Policy" lastUpdated="April 2026">
      <Section title="1. Introduction">
        Meridian Registry ("we", "our", "us") operates a centralized entity resolution
        platform for FMCG retail outlets. This Privacy Policy explains how we collect,
        use, and protect information when you use our services.
      </Section>

      <Section title="2. Data we collect">
        <Bullets items={[
          { label: 'Outlet records', text: 'business names, street addresses, tax identifiers, GPS coordinates, municipality, city, outlet format, and chain affiliation data submitted by clients via spreadsheet files.' },
          { label: 'Client business data', text: 'company name and API key metadata. API keys are stored using cryptographic hashing — we never store raw keys.' },
          { label: 'Matching and review data', text: 'match confidence scores, match methods, review queue decisions, and training signals derived from human review decisions.' },
          { label: 'Temporal data', text: 'processing records, outlet sighting history, and cycle-over-cycle coverage changes.' },
          { label: 'Usage analytics', text: 'anonymized product analytics to improve the platform experience. Error tracking to identify and resolve issues. API request counts and rate-limiting data.' },
          { label: 'Audit data', text: 'records of user actions including data uploads, review decisions, and administrative changes, with timestamps and user attribution.' },
        ]} />
      </Section>

      <Section title="3. How we use your data">
        <Bullets items={[
          { label: 'Normalization', text: 'submitted outlet names, addresses, and identifiers are standardized to enable accurate comparison across client databases. This includes transliteration between writing systems and address formatting.' },
          { label: 'Entity resolution', text: 'a multi-layer matching engine identifies duplicate outlets across databases using business identifiers, name and address similarity, and GPS proximity. High-confidence matches are confirmed automatically; uncertain matches are routed to human review.' },
          { label: 'Registry maintenance', text: 'each unique physical outlet receives a permanent Census ID. Census IDs are never reassigned. Outlet lifecycle is tracked to detect new outlets, probable closures, and coverage changes.' },
          { label: 'Distribution gap detection', text: 'outlets covered by other sources but absent from your database are identified as distribution gaps. Only aggregate counts are shown — never the identity of specific sources.' },
          { label: 'Service improvement', text: 'aggregate review decisions improve matching accuracy over time, reducing the volume of items requiring manual review.' },
        ]} />
      </Section>

      <Section title="4. Data sharing">
        <L>We never share individual client data with other clients.</L> When client data
        is ingested into Meridian, it contributes to the canonical registry — but no
        client can access another client's raw submissions, internal IDs, or proprietary
        outlet classifications. Distribution gap detection reveals only aggregate
        appearance counts (e.g., "seen by 3 clients"), never which clients reported a
        given outlet. The Meridian registry is a separate, unified data layer that
        belongs to Meridian. Authenticated API endpoints return only Meridian canonical
        records, not other clients' raw data.
      </Section>

      <Section title="5. Data retention">
        Outlet records and Census IDs are retained indefinitely as part of the canonical
        registry to maintain Census ID permanence and temporal tracking (outlet sighting
        history). Client account data is retained for the duration of the service
        agreement and for 12 months thereafter. API key usage metadata (request count,
        last used timestamp) is retained for the lifetime of the key. Match training
        signals (review decisions) are retained to support pattern propagation and
        threshold learning. You may request deletion of your submitted data by
        contacting us, subject to our obligation to maintain registry integrity.
      </Section>

      <Section title="6. Data security">
        We implement industry-standard security measures:
        <Bullets items={[
          { text: 'API keys are cryptographically hashed — raw keys are shown once at creation and never stored' },
          { text: 'Rate limiting protects all API endpoints from abuse' },
          { text: 'All data is stored in encrypted, access-controlled database infrastructure' },
          { text: 'All communication uses HTTPS encryption in transit' },
          { text: 'Role-based access controls enforce data isolation between client organisations' },
          { text: 'Full audit logging tracks all significant platform actions' },
        ]} />
      </Section>

      <Section title="7. Contact">
        For privacy-related questions or data requests, contact us at{' '}
        <A href="mailto:admin@meridianregistry.com">admin@meridianregistry.com</A>.
      </Section>
    </LegalPageLayout>
  );
}
